import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import {
  buildRatingValue,
  type RatingComponentConfig,
  type RatingState,
} from '../components/rating/ratingUtils';

interface FeedbackRatingSubmitAreaProps {
  config: RatingComponentConfig;
  ratingState: RatingState;
  selectedValue: number | null;
  submitted: boolean;
  continueToNext: () => void;
  onEdit: () => void;
  submitValueAndGoNext: (componentId: string, value: unknown) => void;
}

const FeedbackRatingSubmitArea: React.FC<FeedbackRatingSubmitAreaProps> = ({
  config,
  continueToNext,
  onEdit,
  ratingState,
  selectedValue,
  submitted,
  submitValueAndGoNext,
}) => {
  const canSubmit = selectedValue !== null;
  const allowResubmit = config.allowResubmit ?? true;
  const scoreLabel =
    config.type === 'csatForm'
      ? (config.options.find((option) => option.value === selectedValue)?.label ??
        String(selectedValue))
      : String(selectedValue);
  const buttonLabel = submitted
    ? (config.submittedActionLabel ?? 'Continue')
    : (config.actionLabel ?? 'Submit');

  return (
    <Stack
      spacing={1.25}
      sx={{
        bgcolor: 'var(--cx-brand-surface)',
        bottom: 0,
        left: '50%',
        maxWidth: 393,
        px: 2,
        py: 2,
        position: 'fixed',
        transform: 'translateX(-50%)',
        width: '100%',
        zIndex: 4,
        ...(config.submitPlacement
          ? {
              bgcolor: 'transparent',
              left: 'auto',
              maxWidth: 'none',
              px: 0,
              py: 0,
              position: config.submitPlacement === 'sticky' ? 'sticky' : 'static',
              transform: 'none',
            }
          : {}),
      }}
    >
      {submitted ? (
        <Box
          sx={{
            bgcolor: 'var(--cx-brand-primary-soft)',
            border: '1px solid var(--cx-brand-border)',
            borderRadius: 2,
            p: 1.25,
          }}
        >
          <Typography variant="subtitle2" sx={{ color: 'var(--cx-brand-text)', fontWeight: 600 }}>
            {config.submittedTitle ?? 'Feedback already submitted'}
            {' · '}
            {scoreLabel}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--cx-brand-text-muted)', mt: 0.35 }}>
            {config.submittedBody ??
              (allowResubmit
                ? 'You can update your answer or continue to the next step.'
                : 'Your previous answer is locked for this visit.')}
          </Typography>
        </Box>
      ) : null}

      {submitted && allowResubmit ? (
        <Button
          fullWidth
          onClick={onEdit}
          sx={{
            borderColor: 'var(--cx-brand-button-border)',
            bgcolor: 'var(--cx-brand-button-bg)',
            color: 'var(--cx-brand-button-text)',
            '&:hover': { bgcolor: 'var(--cx-brand-button-hover-bg)' },
            fontWeight: 600,
            textTransform: 'none',
          }}
          type="button"
          variant="outlined"
        >
          {config.editResponseLabel ?? 'Edit response'}
        </Button>
      ) : null}

      <Button
        disabled={!canSubmit}
        fullWidth
        onClick={() => {
          if (selectedValue === null) return;
          if (submitted) {
            continueToNext();
            return;
          }

          submitValueAndGoNext(
            config.id,
            buildRatingValue(config, ratingState, selectedValue, true)
          );
        }}
        sx={{
          bgcolor: 'var(--cx-brand-primary)',
          border: '1px solid',
          borderColor: 'var(--cx-brand-primary)',
          color: 'var(--cx-brand-primary-contrast)',
          fontSize: 15,
          lineHeight: '26px',
          fontWeight: 600,
          textTransform: 'none',
          height: 48,
          minHeight: 48,
          py: 1,
          '&:disabled': {
            bgcolor: 'var(--cx-brand-primary)',
            borderColor: 'var(--cx-brand-primary)',
            color: 'var(--cx-brand-primary-contrast)',
            opacity: 1,
          },
          '&:hover': { bgcolor: 'var(--cx-brand-primary-dark)' },
        }}
        type="button"
        variant="contained"
      >
        {buttonLabel}
      </Button>
    </Stack>
  );
};

export default FeedbackRatingSubmitArea;
